import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Box,
  Typography,
  IconButton,
  Alert,
  Divider,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import AnswerContentEditor from "./AnswerContentEditor";
import { cleanupOrphanNestedQuestionsRecursive } from "../../utils/templateKeyUtils";
import { getQuestionTypeLabel } from "../../utils/questionUtils";

const QUESTION_TYPES = [
  "Text",
  "TextArea",
  "Number",
  "Date",
  "Time",
  "DateTime",
  "RadioButton",
  "DropDown",
  "Checkbox",
  "LocationSelector",
  "Signature",
];

/**
 * QuestionFormModal - Modal form for adding/editing questions
 *
 * Features:
 * - Type-based field visibility
 * - Options management (RadioButton/DropDown)
 * - Answer branches (optional, for dynamic content)
 * - Repeatable content (for repeatable fields)
 * - Duplicate key detection
 */
const QuestionFormModal = ({
  open,
  question,
  parentContext,
  allQuestions,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState({
    key: "",
    displayText: "",
    description: "",
    type: "Text",
    repeatable: false,
    listType: "ordered", // Default: numbered list (ol)
    required: false,
    options: [],
    answers: [],
    placeholder: "",
    helperText: "",
    dependsOn: null, // For LocationSelector cascade
    source: null, // For LocationSelector: 'il', 'ilce', 'mahalle'
    order: 0,
    step: 0,
    _isNested: false,
  });

  const [errors, setErrors] = useState({});
  const [editingAnswer, setEditingAnswer] = useState(null);
  const [showAnswerEditor, setShowAnswerEditor] = useState(false);

  // Generate temporary ID for new questions
  const generateTempId = () => {
    return `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  };

  // Load existing question data
  useEffect(() => {
    // Check if this is a nested question
    const isNested =
      question?._isNested || !!parentContext || !!question?.parentId;

    if (question) {
      const loadedDefaultValue = question.defaultValue !== undefined && question.defaultValue !== null 
        ? String(question.defaultValue) // Convert to string but preserve exact format
        : (question.defaultValue === '' ? '' : '');
      
      const questionType = question.type || "Text";
      
      const newFormData = {
        _id: question._id || null,
        _tempId: question._tempId || question._id || generateTempId(), // Preserve or generate temp ID
        key: question.key || "",
        displayText: question.displayText || "",
        description: question.description || "",
        type: questionType,
        repeatable: isNested ? false : question.repeatable || false, // Nested questions cannot be repeatable
        listType: question.listType || "ordered", // Default: numbered list
        required: question.required || false,
        options: question.options || [],
        answers: question.answers || [],
        placeholder: question.placeholder || "",
        helperText: question.helperText || "",
        dependsOn: question.dependsOn || null,
        source: question.source || null,
        // Preserve defaultValue from question (could be null, empty string, or a value)
        // CRITICAL: Preserve the exact value as-is for all types (Number, Date, DateTime, etc.)
        defaultValue: loadedDefaultValue,
        order: question.order !== undefined && question.order !== null ? question.order : 0,
        step: question.step !== undefined && question.step !== null ? question.step : 0,
        _isNested: isNested,
      };
      
      setFormData(newFormData);
    } else {
      // Reset for new question - generate temp ID immediately
      const isNestedNew = !!parentContext;
      setFormData({
        _id: null,
        _tempId: generateTempId(), // Generate temp ID for new question
        key: "",
        displayText: "",
        description: "",
        type: "Text",
        repeatable: false,
        listType: "ordered", // Default: numbered list
        required: false,
        options: [],
        answers: [],
        placeholder: "",
        helperText: "",
        dependsOn: null,
        source: null,
        defaultValue: "",
        order: 0,
        step: 0,
        _isNested: isNestedNew,
      });
    }
    setErrors({});
  }, [question, open, parentContext]);

  // Type-based field visibility
  const showOptions = [
    "RadioButton",
    "DropDown",
    "Checkbox",
  ].includes(formData.type);
  const showAnswers = ["RadioButton", "DropDown"].includes(formData.type);
  
  // Show defaultValue for appropriate question types
  const showDefaultValue = [
    "Text",
    "TextArea",
    "Number",
    "Date",
    "Time",
    "DateTime",
    "RadioButton",
    "DropDown",
    "Checkbox",
    "LocationSelector",
  ].includes(formData.type);
  
  // DefaultValue input type based on question type
  // RadioButton/DropDown: defaultValue set via checkboxes in Answer Branches
  // Checkbox: defaultValue set via checkboxes in Options section (comma-separated string)
  const isDefaultValueText = !["RadioButton", "DropDown", "Checkbox"].includes(formData.type);

  // Validate form
  const validate = () => {
    const newErrors = {};

    if (!formData.key || formData.key.trim() === "") {
      newErrors.key = "Key zorunludur";
    } else if (!/^[A-Za-z][A-Za-z0-9_]*$/.test(formData.key)) {
      newErrors.key =
        "Key sadece harf, rakam ve alt çizgi içerebilir ve harf ile başlamalıdır";
    }

    if (!formData.displayText || formData.displayText.trim() === "") {
      newErrors.displayText = "Görünen metin zorunludur";
    }

    // Check for duplicate key
    const duplicate = allQuestions.find(
      (q) =>
        q.key === formData.key &&
        (q._tempId || q._id) !== (question?._tempId || question?._id)
    );
    if (duplicate) {
      newErrors.key = "Bu key zaten kullanılıyor!";
    }

    // Type-based validation
    if (
      showOptions &&
      formData.options.length === 0 &&
      !["LocationSelector"].includes(formData.type)
    ) {
      newErrors.options =
        "RadioButton, DropDown ve Checkbox için en az bir seçenek gereklidir";
    }

    // Validate options
    if (showOptions && formData.options.length > 0) {
      formData.options.forEach((option, index) => {
        if (!option.value || option.value.trim() === "") {
          newErrors[`option_${index}_value`] = "Value zorunludur";
        }
        if (!option.displayText || option.displayText.trim() === "") {
          newErrors[`option_${index}_displayText`] = "Display Text zorunludur";
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validate()) {
      return;
    }

    // Clean up empty answers if no content/paragraph defined
    const cleanedAnswers = formData.answers
      ? formData.answers.filter(
          (answer) =>
            answer.content?.paragraph ||
            (answer.content?.nestedQuestions &&
              answer.content.nestedQuestions.length > 0)
        )
      : [];

    const isRepeatable = formData.repeatable && !formData._isNested;
    
    // CRITICAL: For repeatable questions, always use formData.listType (don't default to 'ordered' if it's explicitly set)
    // Only default to 'ordered' if listType is truly undefined/null/empty
    const finalListType = isRepeatable 
      ? (formData.listType && formData.listType.trim() !== '' ? formData.listType : 'ordered')
      : undefined;
    
    const questionData = {
      _id: formData._id || null, // Preserve database ID if exists
      _tempId: formData._tempId || null, // Preserve temp ID (always exists for new questions)
      key: formData.key.trim(),
      displayText: formData.displayText.trim(),
      description: formData.description?.trim() || "",
      type: formData.type,
      repeatable: isRepeatable, // Only root questions can be repeatable
      listType: finalListType, // Only for repeatable questions
      required: formData.required,
      options: showOptions ? formData.options : undefined,
      answers:
        showAnswers && cleanedAnswers.length > 0 ? cleanedAnswers : undefined,
      placeholder: formData.placeholder?.trim() || "",
      helperText: formData.helperText?.trim() || "",
      dependsOn: formData.type === 'LocationSelector' ? (formData.dependsOn || null) : null,
      source: formData.type === 'LocationSelector' ? (formData.source || null) : null,
      // CRITICAL: Preserve defaultValue exactly as entered by user for all types
      // For Number, Date, DateTime, Time: preserve the exact string value (don't trim - might break format)
      // For Checkbox: preserve comma-separated string (don't trim - might break format)
      // For RadioButton, DropDown: preserve the exact option value (don't trim - might break format)
      // Only set to null if truly undefined/null
      // IMPORTANT: Empty string is valid (means no default), but we should preserve it
      defaultValue: formData.defaultValue !== undefined && formData.defaultValue !== null 
        ? String(formData.defaultValue) // Convert to string but preserve exact format (don't trim)
        : (formData.defaultValue === '' ? '' : null), // Preserve empty string if explicitly set
      order: formData.order !== undefined && formData.order !== null ? Number(formData.order) : 0,
      // Step: Only for root questions (not nested)
      step: !formData._isNested ? (formData.step !== undefined && formData.step !== null ? Number(formData.step) : 0) : undefined,
    };

      onSave(questionData);
  };

  // Add option - generate unique ID for stable key
  const handleAddOption = () => {
    setFormData((prev) => ({
      ...prev,
      options: [...prev.options, { 
        value: "", 
        displayText: "",
        _tempId: `option-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      }],
    }));
  };

  // Update option - useCallback to prevent unnecessary re-renders and focus loss
  const handleUpdateOption = useCallback((index, field, value) => {
    setFormData((prev) => {
      const updated = [...prev.options];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, options: updated };
    });
  }, []);

  // Delete option
  const handleDeleteOption = (index) => {
    setFormData((prev) => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index),
    }));
  };

  // Add answer branch
  const handleAddAnswer = (optionValue, optionDisplayText) => {
    const existingAnswer = formData.answers?.find(
      (a) => a.value === optionValue
    );
    if (existingAnswer) {
      return; // Already exists
    }

    setFormData((prev) => ({
      ...prev,
      answers: [
        ...(prev.answers || []),
        {
          value: optionValue,
          displayText: optionDisplayText,
          content: {
            paragraph: "",
            nestedQuestions: [],
          },
        },
      ],
    }));
  };

  // Handle edit answer content
  const handleEditAnswerContent = (answer) => {
    // Add parentId to answer if not present (for nested questions)
    const answerWithParent = {
      ...answer,
      parentId: formData._id || formData._tempId || null,
    };
    setEditingAnswer(answerWithParent);
    setShowAnswerEditor(true);
  };

  // Handle save answer content (from AnswerContentEditor)
  const handleSaveAnswerContent = (updatedAnswer) => {
    // Update answer in answers array
    const allAnswers = (formData.answers || []).map((answer) =>
      answer.value === updatedAnswer.value ? updatedAnswer : answer
    );

    // Recursive cleanup for all answer branches (including nested answer branches)
    // This cleans up orphan nested questions at all levels
    const cleanedAnswers = cleanupOrphanNestedQuestionsRecursive(allAnswers);

    setFormData((prev) => ({
      ...prev,
      answers: cleanedAnswers,
    }));
    setShowAnswerEditor(false);
    setEditingAnswer(null);
  };

  // Remove answer branch
  const handleRemoveAnswer = (answerValue) => {
    setFormData((prev) => ({
      ...prev,
      answers: (prev.answers || []).filter(
        (answer) => answer.value !== answerValue
      ),
    }));
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {question ? "Soru Düzenle" : "Yeni Soru Ekle"}
          {parentContext && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
              (Alt soru)
            </Typography>
          )}
        </DialogTitle>

        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
            {/* Key */}
            <TextField
              label="Key"
              value={formData.key}
              onChange={(e) =>
                setFormData({ ...formData, key: e.target.value })
              }
              required
              disabled
              error={!!errors.key}
              helperText={
                errors.key ||
                "Key otomatik olarak algılanan {{key}} değerinden doldurulur"
              }
              fullWidth
            />

            {/* Display Text */}
            <TextField
              label="Görünen Metin"
              value={formData.displayText}
              onChange={(e) =>
                setFormData({ ...formData, displayText: e.target.value })
              }
              required
              error={!!errors.displayText}
              helperText={errors.displayText || "UI'da gösterilecek etiket"}
              fullWidth
            />

            {/* Description */}
            <TextField
              label="Açıklama"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              multiline
              rows={2}
              helperText="Tooltip olarak gösterilecek açıklama (optional)"
              fullWidth
            />

            {/* Type */}
            <FormControl fullWidth>
              <InputLabel>Soru Tipi</InputLabel>
              <Select
                value={formData.type}
                onChange={(e) => {
                  const newType = e.target.value;
                  // Reset type-specific fields when type changes
                  // ❗ Tipi kullanıcı değiştirdiği için burada defaultValue'yu SİLİYORUZ
                  // (Number → Text, Date → Number vs.)
                  setFormData((prev) => ({
                    ...prev,
                    type: newType,
                    defaultValue: "", // Clear defaultValue when user changes type
                    options: [
                      "RadioButton",
                      "DropDown",
                      "Checkbox",
                    ].includes(newType)
                      ? prev.options
                      : [],
                    answers: ["RadioButton", "DropDown"].includes(newType)
                      ? prev.answers
                      : [],
                  }));
                }}
                label="Soru Tipi">
                {QUESTION_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {getQuestionTypeLabel(type)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Repeatable - Only for root questions */}
            {!formData._isNested && (
              <>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.repeatable}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          repeatable: e.target.checked,
                          // Reset listType if repeatable is disabled
                          listType: e.target.checked ? formData.listType : "ordered",
                        });
                      }}
                    />
                  }
                  label="Çoklu Satır (Excel ile import/export)"
                />
                
                {/* List Type - Only shown when repeatable is enabled */}
                {formData.repeatable && (
                  <FormControl fullWidth sx={{ mt: 1 }}>
                    <InputLabel>Liste Tipi</InputLabel>
                    <Select
                      value={formData.listType || 'ordered'}
                      onChange={(e) =>
                        setFormData({ ...formData, listType: e.target.value })
                      }
                      label="Liste Tipi"
                    >
                      <MenuItem value="ordered">Numaralı Liste (1, 2, 3...)</MenuItem>
                      <MenuItem value="unordered">Madde İşaretli Liste (•, •, •...)</MenuItem>
                      <MenuItem value="none">Yok (Alt Satıra Geç)</MenuItem>
                    </Select>
                  </FormControl>
                )}
              </>
            )}

            {/* Required */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.required}
                  onChange={(e) =>
                    setFormData({ ...formData, required: e.target.checked })
                  }
                />
              }
              label="Zorunlu Alan"
            />

            {/* Default Value - Only for appropriate question types */}
            {/* RadioButton/DropDown: defaultValue set via checkboxes in Answer Branches section */}
            {/* Checkbox: defaultValue set via checkboxes in Options section */}
            {showDefaultValue && isDefaultValueText && (
              <TextField
                fullWidth
                multiline={["Text", "TextArea", "LocationSelector"].includes(formData.type)}
                rows={["Text", "TextArea", "LocationSelector"].includes(formData.type) ? 3 : 1}
                label="Varsayılan Değer (İsteğe Bağlı)"
                value={formData.defaultValue !== undefined && formData.defaultValue !== null ? String(formData.defaultValue) : ""}
                onChange={(e) =>
                  setFormData({ ...formData, defaultValue: e.target.value })
                }
                placeholder={
                  // No placeholder for date/time pickers (browser native pickers)
                  formData.type === "Date" || formData.type === "Time" || formData.type === "DateTime"
                    ? ""
                    : formData.type === "Number" 
                    ? "Sayısal değer girin (örn: 100 veya 123.45)"
                    : formData.type === "LocationSelector"
                    ? "JSON formatında değer girin"
                    : "Soru için varsayılan değer girin (uzun metinler için)"
                }
                helperText="Bu değer, cevap formunda otomatik olarak doldurulacaktır"
                sx={{ mt: 2 }}
                type={
                  formData.type === "Number" ? "text" :
                  formData.type === "Date" ? "date" :
                  formData.type === "Time" ? "time" :
                  formData.type === "DateTime" ? "datetime-local" :
                  "text"
                }
                inputProps={
                  formData.type === "Number" 
                    ? { pattern: "[0-9.,]+", inputMode: "numeric" }
                    : undefined
                }
              />
            )}

            <Divider />

            {/* Options (for RadioButton/DropDown/Checkbox) */}
            {showOptions && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Seçenekler{" "}
                  {formData.type !== "LocationSelector" && (
                    <Typography component="span" color="error">
                      (zorunlu)
                    </Typography>
                  )}
                </Typography>
                {errors.options && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {errors.options}
                  </Alert>
                )}
                {/* Calculate defaultValueArray once to prevent re-renders */}
                {(() => {
                  // Calculate defaultValueArray once outside map
                  // Ensure defaultValue is trimmed before splitting
                  const defaultValueArray = formData.type === "Checkbox" && formData.defaultValue 
                    ? formData.defaultValue.trim().split(',').map(v => v.trim()).filter(Boolean)
                    : [];
                  
                  return formData.options.map((option, index) => {
                    // For Checkbox: Check if this option is in defaultValue (comma-separated string)
                    // Compare trimmed values to ensure consistency
                    const optionValueTrimmed = option.value?.trim();
                    const isDefaultChecked = formData.type === "Checkbox" && defaultValueArray.includes(optionValueTrimmed);
                    
                    return (
                      <Box
                        key={option._id || option._tempId || `option-${index}`}
                        sx={{
                          display: "flex",
                          gap: 1,
                          mb: 1,
                          alignItems: "flex-start",
                        }}>
                        <TextField
                          size="small"
                          label="Display Text"
                          value={option.displayText}
                          onChange={(e) =>
                            handleUpdateOption(index, "displayText", e.target.value)
                          }
                          error={!!errors[`option_${index}_displayText`]}
                          helperText={errors[`option_${index}_displayText`]}
                          sx={{ flex: 1 }}
                        />
                        <TextField
                          size="small"
                          label="Value"
                          value={option.value}
                          onChange={(e) =>
                            handleUpdateOption(index, "value", e.target.value)
                          }
                          error={!!errors[`option_${index}_value`]}
                          helperText={errors[`option_${index}_value`]}
                          sx={{ flex: 1 }}
                        />

                        {/* Default Value Checkbox for Checkbox question type */}
                        {formData.type === "Checkbox" && (
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={isDefaultChecked}
                                onChange={(e) => {
                                  // Use functional update to avoid re-rendering all options
                                  setFormData((prev) => {
                                    const currentDefaultValueArray = prev.defaultValue 
                                      ? prev.defaultValue.split(',').map(v => v.trim()).filter(Boolean)
                                      : [];
                                    let newDefaultValueArray = [...currentDefaultValueArray];
                                    
                                    if (e.target.checked) {
                                      // Add to array if not already present
                                      // Use trimmed value for consistency
                                      const optionValueTrimmed = option.value?.trim();
                                      if (optionValueTrimmed && !newDefaultValueArray.includes(optionValueTrimmed)) {
                                        newDefaultValueArray.push(optionValueTrimmed);
                                      }
                                    } else {
                                      // Remove from array
                                      // Use trimmed value for consistency
                                      const optionValueTrimmed = option.value?.trim();
                                      newDefaultValueArray = newDefaultValueArray.filter(v => v !== optionValueTrimmed);
                                    }
                                    
                                    return {
                                      ...prev,
                                      defaultValue: newDefaultValueArray.join(',')
                                    };
                                  });
                                }}
                              />
                            }
                            label={
                              <Typography variant="caption" color="text.secondary">
                                Varsayılan
                              </Typography>
                            }
                            sx={{ minWidth: 100 }}
                          />
                        )}

                        <IconButton
                          size="small"
                          onClick={() => handleDeleteOption(index)}
                          color="error">
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    );
                  });
                })()}
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddOption}
                  variant="outlined">
                  Seçenek Ekle
                </Button>
              </Box>
            )}

            {/* Answer Branches (for RadioButton/DropDown) */}
            {showAnswers && formData.options.length > 0 && (
              <Box>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Cevap Dallanmaları (İsteğe Bağlı)
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Her cevap için farklı paragraf şablonu ve alt sorular
                  tanımlayabilirsiniz. Bu, cevaba göre dinamik içerik
                  oluşturmanızı sağlar.
                </Alert>

                {formData.options.map((option, index) => {
                  const answer = formData.answers?.find(
                    (a) => a.value === option.value
                  );
                  return (
                    <Box
                      key={option._id || option.value || `answer-option-${index}`}
                      sx={{
                        mb: 2,
                        p: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                      }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 1,
                        }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Typography variant="subtitle2">
                            ➤ {option.displayText || option.value}
                          </Typography>
                          {/* Default Value Checkbox */}
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={(() => {
                                  const defaultValueTrimmed = formData.defaultValue?.trim() || '';
                                  const optionValueTrimmed = option.value?.trim() || '';
                                  return defaultValueTrimmed === optionValueTrimmed;
                                })()}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    // Use trimmed value for consistency
                                    const optionValueTrimmed = option.value?.trim() || '';
                                    setFormData({ ...formData, defaultValue: optionValueTrimmed });
                                  } else {
                                    setFormData({ ...formData, defaultValue: "" });
                                  }
                                }}
                              />
                            }
                            label={
                              <Typography variant="caption" color="text.secondary">
                                Varsayılan
                              </Typography>
                            }
                          />
                        </Box>
                        {answer ? (
                          <Button
                            size="small"
                            color="error"
                            onClick={() => handleRemoveAnswer(option.value)}>
                            Kaldır
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() =>
                              handleAddAnswer(option.value, option.displayText)
                            }>
                            Dallanma Ekle
                          </Button>
                        )}
                      </Box>

                      {answer && (
                        <Box sx={{ mt: 2 }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              mb: 1,
                            }}>
                            <Typography variant="body2" color="text.secondary">
                              {answer.content?.paragraph
                                ? `Paragraf: ${answer.content.paragraph.substring(
                                    0,
                                    50
                                  )}...`
                                : "Paragraf tanımlanmadı"}
                            </Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<EditIcon />}
                              onClick={() => handleEditAnswerContent(answer)}>
                              Düzenle
                            </Button>
                          </Box>
                          {answer.content?.nestedQuestions?.length > 0 && (
                            <Typography
                              variant="caption"
                              color="text.secondary">
                              {answer.content.nestedQuestions.length} alt soru
                              tanımlı
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}

            {/* Order & Step */}
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: "flex", gap: 2 }}>
              {/* Order - Always visible */}
              <TextField
                label="Sıra (Order)"
                type="number"
                value={formData.order !== undefined && formData.order !== null ? formData.order : 0}
                onChange={(e) => {
                  const value = parseInt(e.target.value, 10);
                  setFormData({ 
                    ...formData, 
                    order: isNaN(value) ? 0 : Math.max(0, value) 
                  });
                }}
                helperText="Soru sıralaması (0'dan başlar)"
                inputProps={{ min: 0 }}
                fullWidth
              />
              
              {/* Step - Only for root questions */}
              {!formData._isNested && (
                <TextField
                  label="Adım (Step)"
                  type="number"
                  value={formData.step !== undefined && formData.step !== null ? formData.step : 0}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10);
                    setFormData({ 
                      ...formData, 
                      step: isNaN(value) ? 0 : Math.max(0, value) 
                    });
                  }}
                  helperText="Wizard adım numarası (0'dan başlar)"
                  inputProps={{ min: 0 }}
                  fullWidth
                />
              )}
            </Box>

            {/* LocationSelector specific fields */}
            {formData.type === 'LocationSelector' && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Konum Seçici Ayarları
                </Typography>
                <FormControl fullWidth>
                  <InputLabel>Kaynak (Source)</InputLabel>
                  <Select
                    value={formData.source || ''}
                    onChange={(e) =>
                      setFormData({ ...formData, source: e.target.value || null })
                    }
                    label="Kaynak (Source)"
                  >
                    <MenuItem value="il">İl</MenuItem>
                    <MenuItem value="ilce">İlçe</MenuItem>
                    <MenuItem value="mahalle">Mahalle</MenuItem>
                  </Select>
                </FormControl>
                {formData.source && formData.source !== 'il' && (
                  <FormControl fullWidth sx={{ mt: 2 }}>
                    <InputLabel>Bağlı Soru (Depends On)</InputLabel>
                    <Select
                      value={formData.dependsOn || ''}
                      onChange={(e) =>
                        setFormData({ ...formData, dependsOn: e.target.value || null })
                      }
                      label="Bağlı Soru (Depends On)"
                    >
                      <MenuItem value="">Yok</MenuItem>
                      {allQuestions
                        .filter((q) => {
                          // İlçe için: İl tipinde LocationSelector
                          if (formData.source === 'ilce') {
                            return q.type === 'LocationSelector' && q.source === 'il';
                          }
                          // Mahalle için: İlçe tipinde LocationSelector
                          if (formData.source === 'mahalle') {
                            return q.type === 'LocationSelector' && q.source === 'ilce';
                          }
                          return false;
                        })
                        .map((q) => (
                          <MenuItem
                            key={q._id || q._tempId || q.key}
                            value={q._id || q._tempId || q.key}
                          >
                            {q.displayText} ({q.key})
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                )}
                {formData.source && formData.source !== 'il' && !formData.dependsOn && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    {formData.source === 'ilce'
                      ? 'İlçe seçmek için önce bir İl sorusu seçmelisiniz.'
                      : 'Mahalle seçmek için önce bir İlçe sorusu seçmelisiniz.'}
                  </Alert>
                )}
              </>
            )}

            {/* Placeholder & Helper Text */}
            <Divider sx={{ my: 2 }} />
            <TextField
              label="Placeholder"
              value={formData.placeholder}
              onChange={(e) =>
                setFormData({ ...formData, placeholder: e.target.value })
              }
              helperText="Input placeholder metni (optional)"
              fullWidth
            />
            <TextField
              label="Helper Text"
              value={formData.helperText}
              onChange={(e) =>
                setFormData({ ...formData, helperText: e.target.value })
              }
              helperText="Input altında gösterilecek yardımcı metin (optional)"
              fullWidth
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>İptal</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            Kaydet
          </Button>
        </DialogActions>
      </Dialog>

      {/* Answer Content Editor */}
      {showAnswerEditor && editingAnswer && (
        <AnswerContentEditor
          open={showAnswerEditor}
          answer={editingAnswer}
          allQuestions={allQuestions}
          parentQuestionId={formData._id || formData._tempId || null}
          onClose={() => {
            setShowAnswerEditor(false);
            setEditingAnswer(null);
          }}
          onSave={handleSaveAnswerContent}
        />
      )}
    </>
  );
};

QuestionFormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  question: PropTypes.object,
  parentContext: PropTypes.shape({
    parentId: PropTypes.string,
    answerValue: PropTypes.string,
  }),
  allQuestions: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

QuestionFormModal.defaultProps = {
  question: null,
  parentContext: null,
};

export default QuestionFormModal;
